import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from './FormManager';
import './PricingPage.css';
import { Helmet } from 'react-helmet';

const PricingPage = () => {
  const { t } = useTranslation();
  const { openGeneralForm } = useForm();
  
  // Refs for animations
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const pricingCardRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);
  
  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const animateOnScroll = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    };
    
    const observer = new IntersectionObserver(animateOnScroll, observerOptions);
    
    // Observe all refs
    const refs = [heroRef, titleRef, descriptionRef, pricingCardRef, faqRef, ctaRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    // Observe FAQ items
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach((item, index) => {
      observer.observe(item);
    });
    
    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
      
      faqItems.forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []);
  
  // FAQ toggle functionality
  const toggleFaq = (index) => {
    const faqItem = document.querySelector(`.faq-item:nth-child(${index + 1})`);
    if (faqItem) {
      faqItem.classList.toggle('active');
    }
  };
  
  // FAQ data - using translation keys from language files
  const getFaqItems = () => [
    {
      question: t('faqQuestion1'),
      answer: t('faqAnswer1')
    },
    {
      question: t('faqQuestion2'),
      answer: t('faqAnswer2')
    },
    {
      question: t('faqQuestion3'),
      answer: t('faqAnswer3')
    },
    {
      question: t('faqQuestion4'),
      answer: t('faqAnswer4')
    },
    {
      question: t('faqQuestion5'),
      answer: t('faqAnswer5')
    }
  ];
  // Get translated FAQ items
  const faqItems = getFaqItems();
  
  return (
    <>
      <Helmet>
        <title>Preise – Enzlo Digitalagentur: Websites, SEO, Ads & Buchungssysteme in Frankfurt, Darmstadt, Stuttgart, Ulm</title>
        <meta name="description" content="Transparente Preise für professionelle Websites, SEO, Google Ads, Online-Buchungssysteme & mehr. Enzlo – Ihre Full-Service Digitalagentur für das Rhein-Main- und Süddeutschland-Gebiet." />
        <link rel="canonical" href="https://enzlo.com/pricing" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Offer",
            "url": "https://enzlo.com/pricing",
            "price": "997",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "seller": {
              "@type": "Organization",
              "name": "Enzlo Digitalagentur",
              "url": "https://enzlo.com/",
              "logo": "https://enzlo.com/logo.png",
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+49 123 456 7890",
                  "contactType": "customer service",
                  "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
                }
              ],
              "address": [
                {
                  "@type": "PostalAddress",
                  "streetAddress": "Eckweg 8",
                  "postalCode": "64354",
                  "addressLocality": "Reinheim",
                  "addressCountry": "DE"
                },
                {
                  "@type": "PostalAddress",
                  "streetAddress": "Zeppelinstraße 20",
                  "postalCode": "73084",
                  "addressLocality": "Salach",
                  "addressCountry": "DE"
                }
              ]
            },
            "itemOffered": {
              "@type": "Service",
              "name": "Full-Service Digitalagentur Leistungen",
              "description": "Webdesign, SEO, Google Ads, Online-Buchungssysteme, Reputation Management, E-Mail Marketing"
            }
          }
        `}</script>
        <script type="application/ld+json">{`
        [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Webdesign Agentur",
            "description": "Professionelle Websites für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm und Umgebung.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "SEO Agentur",
            "description": "Suchmaschinenoptimierung (SEO) für lokale Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Google Ads Agentur",
            "description": "Google Ads Kampagnen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Online Buchungssystem",
            "description": "Online-Buchungslösungen für Dienstleister und Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Reputation Management",
            "description": "Automatisierte Generierung von Google Bewertungen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "E-Mail Marketing",
            "description": "E-Mail Marketing und Newsletter für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          }
        ]
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="pricing-page-hero" ref={heroRef}>
        <div className="pricing-page-background">
          <div className="pricing-page-shape pricing-page-shape-1"></div>
          <div className="pricing-page-shape pricing-page-shape-2"></div>
        </div>
        
        <div className="container">
          <div className="pricing-page-content">
            <h1 className="pricing-page-title" ref={titleRef}>
              {t('pricingTitle')}
            </h1>
            
            <p className="pricing-page-description" ref={descriptionRef}>
              {t('pricingDescription')}
            </p>
          </div>
        </div>
      </section>
      
      {/* Pricing Card Section - REBUILT */}
      <section className="pricing-card-section" ref={pricingCardRef}>
        <div className="gold-accent-1"></div>
        <div className="gold-accent-2"></div>
        <div className="pricing-card-new">
          <div className="pricing-left-column">
            <div className="pricing-card-header-new">
              <div className="original-price">€3000</div>
              <div className="pricing-price-new">
                <span className="currency">€</span>
                <span className="amount">997</span>
                <span className="period">{t('pricingPeriod')}</span>
              </div>
              <div className="pricing-creation-fee">
                      <span className="creation-fee-item">
                        <span className="creation-fee-plus">+</span>
                        <span className="creation-fee-amount">€500</span>
                        <span className="creation-fee-label">{t('pricinglabel')}</span>
                      </span>
              </div>
              
            </div>
            <button className="pricing-cta-new" onClick={openGeneralForm}>
              {t('pricingCta', 'Get Started')} <span className="cta-arrow">→</span>
            </button>
            <p className="cta-note">{t('ctaNote')}</p>
          </div>
          <ul className="pricing-features-new">
            {[1,2,3,4,5,6,7,8].map(i => (
              <li key={i}>
                <span className="feature-icon-new">✓</span>
                <span>{t(`pricingFeature${i}`, `Feature ${i}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      
      {/* Guarantee Section */}
      <section className="guarantee-section">
        <div className="pricing-guarantee-outer">
          <span className="guarantee-icon-new">🛡️</span>
          <div className="guarantee-content">
            <h3>{t('pricingGuarantee', '90-Day Money Back Guarantee')}</h3>
            <p>{t('pricingGuaranteeDetails', 'If you don\'t see measurable results within 90 days, we\'ll refund your investment in full. No questions asked. We\'re confident in our ability to deliver real business growth.')}</p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="pricing-page-faq" ref={faqRef}>
        <div className="container">
          <h2 className="faq-title">{t('faqTitle')}</h2>
          
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <div 
                key={index} 
                className="faq-pricing-item"
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <h3>{item.question}</h3>
                  <span className="faq-toggle">+</span>
                </div>
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Results Section - REBUILT */}
      <section className="results-section-new">
        <div className="results-header-new">
          <h2>{t('resultsTitle', 'Real Results')}</h2>
          <p className="results-subtitle-new">{t('resultsSubtitle', 'What our clients have achieved')}</p>
        </div>
        <div className="results-stats-new">
          <div className="result-card-new">
            <div className="result-value-new">{t('resultsStat1', '+85%')}</div>
            <div className="result-desc-new">{t('resultsStat1Desc', 'Increase in organic traffic')}</div>
            <div className="result-icon-new">📈</div>
          </div>
          <div className="result-card-new">
            <div className="result-value-new">{t('resultsStat2', '3.2x')}</div>
            <div className="result-desc-new">{t('resultsStat2Desc', 'Return on investment')}</div>
            <div className="result-icon-new">💰</div>
          </div>
          <div className="result-card-new">
            <div className="result-value-new">{t('resultsStat3', '-40%')}</div>
            <div className="result-desc-new">{t('resultsStat3Desc', 'Reduction in acquisition costs')}</div>
            <div className="result-icon-new">⏱️</div>
          </div>
        </div>
        <div className="results-note-new">{t('resultsNote', 'Based on average client performance after 12 months')}</div>
      </section>
      
      {/* CTA Section */}
      <section className="pricing-page-cta" ref={ctaRef}>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{t('finalCtaTitle')}</h2>
            <p className="cta-description">{t('finalCtaDescription')}</p>
            <button className="cta-button" onClick={openGeneralForm}>
              <span className="cta-text">{t('finalCtaPrimary')}</span>
              <span className="cta-arrow">→</span>
            </button>
            <p className="cta-note">{t('ctaNote')}</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingPage;
